import { computed, inject, Injectable, signal, Signal, untracked } from '@angular/core';
import { FormQuestionState, FormSectionState, ModuleConfigState, ModuleId, ModuleResult } from '@pixacare/forms';
import { FormBridge } from '@pixacare/forms-angular';
import { ActionStatus } from '@pixacare/shared';
import { Status } from '@shared/models/enums/status.enum';
import { ActionButton } from '@shared/models/menu-actions/action-buttons';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { ModuleResolverInputs } from '../../../models/module-resolver-inputs';
import { EMPTY_AUTO_MODULES } from '../form-grid.config';
import { FormGridActionsContext } from '../models/form-grid-actions-context';
import { FormGridConfig } from '../models/form-grid-config';
import { FormGridConfigBase } from '../models/form-grid-config-base';
import { FormGridEntityKey } from '../models/form-grid-entity-key';
import { FormGridGroupConfigBase } from '../models/form-grid-group-config-base';
import { FormGridGroupMode } from '../models/form-grid-group-mode';
import { FormGridGroupSections } from '../models/form-grid-group-sections';
import { FormInstance } from '../models/form-instance';
import { FormInstanceStatus } from '../models/form-instance-status';
import { FormGridInstanceStore } from './form-grid-instance.store';
import { FormGridModeService } from './form-grid-mode.service';
import { FormGridVisibilityService } from './form-grid-visibility.service';

const EMPTY_MODULES: ModuleConfigState[] = [];

@Injectable()
export class FormGridService {
  private readonly store = inject(FormGridInstanceStore);
  private readonly modeService = inject(FormGridModeService);
  private readonly visibility = inject(FormGridVisibilityService);

  private config: FormGridConfigBase | null = null;

  private readonly _isInitialized = signal(false);
  readonly isInitialized = this._isInitialized.asReadonly();

  readonly HEADER_COLUMN = this.store.HEADER_COLUMN;
  readonly entityColumnKey = this.store.entityColumnKey;
  readonly entities = this.store.entities;

  readonly columnsKey = computed<string[]>(() => {
    if (!this.config) {
      return [];
    }
    return [this.HEADER_COLUMN, ...this.store.entities().map((entity) => this.entityColumnKey(entity.entityKey))];
  });

  readonly groups = computed<readonly FormGridGroupConfigBase[]>(() => {
    const config = this.config;
    if (!config) {
      return [];
    }
    const instanceMap = this.store.instancesMap();
    const entities = this.store.entities();
    return config.formGroups.filter((group) =>
      entities.some((entity) => instanceMap.has(this.store.key(entity.entityKey, group.type))),
    );
  });

  readonly groupedSections = computed<readonly FormGridGroupSections[]>(() =>
    this.groups().map((group) => ({
      type: group.type,
      sectionIds: this.visibility
        .visibleSections(group.type)()
        .map((section) => section.id),
    })),
  );

  readonly isDirty = computed<boolean>(() => this.store.isAnyDirty());

  initialize<TFormType extends string, TEntity extends FormGridEntityKey, TContext>(
    config: FormGridConfig<TFormType, TEntity, TContext>,
  ): void {
    untracked(() => {
      const baseConfig = config as unknown as FormGridConfigBase;
      const newEntityKeys = new Set(config.entities.map((entity) => entity.entityKey));
      const formTypes = baseConfig.formGroups.map((group) => group.type);

      for (const entity of this.store.entities()) {
        if (!newEntityKeys.has(entity.entityKey)) {
          this.store.removeInstancesFor(entity.entityKey, formTypes);
        }
      }

      this.visibility.reset();
      this.config = baseConfig;
      this.modeService.initialize(baseConfig);
      this.loadInstances(baseConfig, config.entities);
      this.store.setEntities(config.entities);
      this._isInitialized.set(true);
    });
  }

  addEntity(entity: FormGridEntityKey, initialMode: FormGridGroupMode = 'readonly'): void {
    if (!this.config) {
      return;
    }
    this.loadInstances(this.config, [entity], initialMode);
    this.store.appendEntity(entity);
  }

  removeEntity(entityKey: string): void {
    if (!this.config) {
      return;
    }
    this.store.removeInstancesFor(
      entityKey,
      this.config.formGroups.map((group) => group.type),
    );
    this.store.dropEntity(entityKey);
  }

  bridge(entityKey: string, formType: string): FormBridge | undefined {
    return this.store.bridge(entityKey, formType);
  }

  date(entityKey: string, formType: string): Date | string | null {
    return this.store.date(entityKey, formType);
  }

  mode(entityKey: string, formType: string): FormGridGroupMode {
    return this.store.mode(entityKey, formType);
  }

  isLoadedEmpty(entityKey: string, formType: string): boolean {
    return this.store.isLoadedEmpty(entityKey, formType);
  }

  instance(entityKey: string, formType: string): FormInstance | undefined {
    return this.store.instance(entityKey, formType);
  }

  status(entityKey: string, formType: string): FormInstanceStatus | undefined {
    return this.store.status(entityKey, formType);
  }

  setStatus(entityKey: string, formType: string, status: FormInstanceStatus): void {
    this.store.setStatus(entityKey, formType, status);
  }

  clearStatus(entityKey: string, formType: string): void {
    this.store.clearStatus(entityKey, formType);
  }

  markSaving(entityKey: string, formType: string): void {
    this.store.setStatus(entityKey, formType, { state: ActionStatus.IN_PROGRESS });
  }

  markSaveResult(entityKey: string, formType: string, success: boolean, message?: string): void {
    if (success) {
      this.modeService.enterReadonly(entityKey, formType);
      this.store.markInstancePristine(entityKey, formType);
      this.store.setStatus(entityKey, formType, { state: ActionStatus.SUCCESS });
    } else {
      this.store.setStatus(entityKey, formType, { state: ActionStatus.ERROR, message });
    }
  }

  moduleInputs(entityKey: string, formType: string): Signal<ModuleResolverInputs> {
    return this.store.moduleInputs(entityKey, formType);
  }

  showTodayLabel(entityKey: string, formType: string): boolean {
    return this.modeService.showTodayLabel(entityKey, formType);
  }

  enterEdit(entityKey: string, formType: string): void {
    this.modeService.enterEdit(entityKey, formType);
  }

  enterReadonly(entityKey: string, formType: string): void {
    this.modeService.enterReadonly(entityKey, formType);
  }

  markInstancePristine(entityKey: string, formType: string): void {
    this.store.markInstancePristine(entityKey, formType);
  }

  enterAdd(entityKey: string, formType: string): void {
    this.modeService.enterAdd(entityKey, formType);
  }

  clearForm(entityKey: string, formType: string): void {
    this.modeService.clearForm(entityKey, formType);
  }

  visibleSections(formType: string): Signal<FormSectionState[]> {
    return this.visibility.visibleSections(formType);
  }

  visibleQuestions(formType: string, sectionId: string): Signal<FormQuestionState[]> {
    return this.visibility.visibleQuestions(formType, sectionId);
  }

  actions(entityKey: string, formType: string): MenuAction[] {
    const group = this.findGroup(formType);
    const entity = this.store.entities().find((candidate) => candidate.entityKey === entityKey);
    if (!group?.actions || !entity) {
      return [];
    }
    const mode = this.store.mode(entityKey, formType);
    const context: FormGridActionsContext<FormGridEntityKey> = {
      entity,
      mode: () => mode,
      isLoadedEmpty: () => this.store.isLoadedEmpty(entityKey, formType),
      editCreatesNewEntry: group.editCreatesNewEntry ?? false,
      enterEdit: () => this.modeService.enterEdit(entityKey, formType),
      enterAdd: () => this.modeService.enterAdd(entityKey, formType),
      clear: () => this.modeService.clearForm(entityKey, formType),
    };
    return group.actions(context);
  }

  actionButtons(entityKey: string, formType: string): ActionButton[] {
    return this.actions(entityKey, formType).map((action): ActionButton => ({
      actionIds: [action.id],
      icon: action.icon,
      title: action.label,
      hint: action.hint,
      appearance: action.appearance ?? (action.status === Status.ERROR ? 'outline-destructive' : undefined),
    }));
  }

  sectionModules(entityKey: string, formType: string, sectionId: string): ModuleConfigState[] {
    const bridge = this.store.bridge(entityKey, formType);
    return bridge?.sections().find((section) => section.id === sectionId)?.modules ?? EMPTY_MODULES;
  }

  questionModules(entityKey: string, formType: string, questionId: string): ModuleConfigState[] {
    const bridge = this.store.bridge(entityKey, formType);
    return bridge?.questions()?.[questionId]?.modules ?? EMPTY_MODULES;
  }

  autoModules(entityKey: string, formType: string): ModuleConfigState[] {
    return this.store.bridge(entityKey, formType)?.autoModules() ?? EMPTY_AUTO_MODULES;
  }

  isSectionHidden(entityKey: string, formType: string, sectionId: string): boolean {
    const bridge = this.store.bridge(entityKey, formType);
    if (!bridge) {
      return false;
    }
    const section = bridge.sections().find((candidate) => candidate.id === sectionId);
    return !section || !!section.isHidden;
  }

  isQuestionHidden(entityKey: string, formType: string, questionId: string): boolean {
    return !!this.store.bridge(entityKey, formType)?.questions()?.[questionId]?.isHidden;
  }

  applyModuleResult(
    entityKey: string,
    formType: string,
    moduleId: ModuleId,
    targetId: string,
    result: ModuleResult,
  ): void {
    this.store.bridge(entityKey, formType)?.applyModuleResult(moduleId, targetId, result);
    const group = this.findGroup(formType);
    const entity = this.store.entities().find((candidate) => candidate.entityKey === entityKey);
    if (group?.onModuleTriggered && entity) {
      group.onModuleTriggered(entity, { moduleId, targetId, result });
    }
  }

  resetModuleResult(entityKey: string, formType: string, moduleId: ModuleId, targetId: string): void {
    this.store.bridge(entityKey, formType)?.resetModuleResult(moduleId, targetId);
    const group = this.findGroup(formType);
    const entity = this.store.entities().find((candidate) => candidate.entityKey === entityKey);
    if (group?.onModuleReset && entity) {
      group.onModuleReset(entity, { moduleId, targetId });
    }
  }

  reportValidity(targets?: readonly { entityKey: string; formType: string }[]): boolean {
    const instances = targets
      ? targets
          .map((target) => this.store.instance(target.entityKey, target.formType))
          .filter((instance): instance is FormInstance => !!instance)
      : this.store.instances();
    let areAllValid = true;
    for (const instance of instances) {
      if (instance.mode() === 'readonly') {
        continue;
      }
      const bridge = this.store.bridge(instance.entityKey, instance.type);
      if (!bridge || bridge.isFormValid()) {
        continue;
      }
      areAllValid = false;
      for (const section of bridge.sections()) {
        if (!section.isValid) {
          bridge.markSectionQuestionsAsVisited(section.id);
        }
      }
      bridge.formGroup()?.markAllAsTouched();
    }
    return areAllValid;
  }

  private loadInstances(
    config: FormGridConfigBase,
    entities: readonly FormGridEntityKey[],
    initialMode: FormGridGroupMode = 'readonly',
  ): void {
    for (const entity of entities) {
      for (const group of config.formGroups) {
        const form = group.form(config.context);
        if (!form) {
          continue;
        }
        if (this.store.hasInstance(entity.entityKey, group.type)) {
          continue;
        }
        // In add mode the bridge applies the form's default answers instead of replaying the entity's
        // last-saved one, and the cell shows today's date as the editing target.
        const isAddMode = initialMode === 'add';
        this.store.createInstance({
          entityKey: entity.entityKey,
          type: group.type,
          form,
          answer: isAddMode ? null : group.answer(entity),
          date: isAddMode ? new Date() : group.date(entity),
          mode: initialMode,
          moduleInputs: group.moduleInputs?.(entity),
          onBecameDirty: (entityKey) => this.store.setStatus(entityKey, group.type, { state: ActionStatus.WAITING }),
        });
      }
    }
  }

  private findGroup(formType: string): FormGridGroupConfigBase | undefined {
    return this.config?.formGroups.find((group) => group.type === formType);
  }
}
